import React from "react";
import {
	toDataSourceRequestString,
	translateDataSourceResultGroups,
	groupBy
} from "@progress/kendo-data-query";
import store from "../../store";
import CryptoJS from "crypto-js";
import { Alert } from "antd";
import moment from "moment";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
	setExpandedState,
	setGroupIds,
} from "@progress/kendo-react-data-tools";
import apiCalls from '../../api/apiCalls';
const filterOperators = {
	text: [
		{ text: "grid.filterContainsOperator", operator: "contains" },
		{ text: "grid.filterNotContainsOperator", operator: "doesnotcontain" },
		{ text: "grid.filterEqOperator", operator: "eq" },
		{ text: "grid.filterNotEqOperator", operator: "neq" },
		{ text: "grid.filterStartsWithOperator", operator: "startswith" },
		{ text: "grid.filterEndsWithOperator", operator: "endswith" },
		{ text: "grid.filterIsEmptyOperator", operator: "isempty" },
		{ text: "grid.filterIsNotEmptyOperator", operator: "isnotempty" },
	],
	numeric: [
		{ text: "grid.filterEqOperator", operator: "eq" },
		{ text: "grid.filterNotEqOperator", operator: "neq" },
	],
	date: [
		{ text: "grid.filterAfterOrEqualOperator", operator: "gte" },
		{ text: "grid.filterAfterOperator", operator: "gt" },
		{ text: "grid.filterBeforeOperator", operator: "lt" },
		{ text: "grid.filterBeforeOrEqualOperator", operator: "lte" },
	],
	datetime: [
		{ text: "grid.filterEqOperator", operator: "eq" },
		{ text: "grid.filterNotEqOperator", operator: "neq" },
		{ text: "grid.filterAfterOrEqualOperator", operator: "gte" },
		{ text: "grid.filterAfterOperator", operator: "gt" },
		{ text: "grid.filterBeforeOperator", operator: "lt" },
		{ text: "grid.filterBeforeOrEqualOperator", operator: "lte" },
	],
	boolean: [{ text: "grid.filterEqOperator", operator: "eq" }],
};

const processWithGroups = (data, group) => {
	const newDataState = groupBy(data, group);
	setGroupIds({
		data: newDataState,
		group: group,
	});
	return newDataState;
};

export function withState(WrappedGrid) {
	return class StatefullGrid extends React.Component {
		initialState = { skip: 0, take: this.props?.pSize || 10 }
		constructor(props) {
			super(props);
			this.state = {
				dataState: this.initialState,
				additionalParams: null,
				data: [],
				group: [],
				isLoading: false,
				collapsedState: [],
				gridStatus: null,
			};
			this.excelRef = React.createRef(); // Kept for direct access if needed
			// Define a stable ref callback for ExcelExport
			this.setExcelRef = exporter => {
				this.excelRef = exporter;
			};
		}
		numberWithCommas(x) {
			x = (typeof x) == 'string' ? x : x.toString();
			let arParts = x.split('.');
			let intPart = parseInt(arParts[0]).toLocaleString();
			let decPart = (arParts.length > 1 ? arParts[1] : '');

			return '' + intPart + (decPart ? ('.' + decPart) : '');
		}

		expandChange = (event) => {
			const item = event.dataItem;
			if (item.groupId) {
				const newCollapsedIds = !event.value
					? [...this.state.collapsedState, item.groupId]
					: this.state.collapsedState.filter(
						(groupId) => groupId !== item.groupId
					);
				this.setState({
					collapsedState: newCollapsedIds,
				});
			}
		};

		onGroupsToggle = () => {
			let collapseAllState = []
			if (this.state.result) {
				for (let i in this.state.result) {
					collapseAllState.push(this.state.result[i].groupId)
				}
			}
			const newCollapsedState = this.state.collapsedState.length
				? []
				:
				collapseAllState
			this.setState({
				collapsedState: newCollapsedState,
			});
		};

		refreshGrid() {
			this.fetchData(this.state.dataState);
		}
		resetToDefault(initialDataState) {
			this.setState({ ...this.state, dataState: initialDataState, group: initialDataState.group })
			this.fetchData(initialDataState)
		}

		_processCellForExport(cell, column, dataItemForRow) {
			if (!cell) return; // Guard if cell is undefined
			// Date formatting
			if (column.filterType === "date" && column?.isShowTime === true && cell.value) {
				cell.value = moment.utc(cell.value).local().format("DD/MM/YYYY hh:mm:ss A");
				cell.format = "dd/mm/yyyy hh:mm:ss AM/PM";
			} else if (column.filterType === "datetime" && cell.value) {
				cell.value = moment.utc(cell.value).local().format("DD/MM/YYYY hh:mm:ss A");
				cell.format = "dd/mm/yyyy hh:mm:ss AM/PM";
			} else if (column.filterType === "date" && cell.value) {
				cell.value = moment.utc(cell.value).local().format("DD/MM/YYYY");
				cell.format = "dd/mm/yyyy";
			}
			if (column.filterType === "numeric" || column.filterType === "number") {
				let numericValue = cell.value;
				if (typeof numericValue === 'string') {
					numericValue = parseFloat(numericValue.replace(/,/g, ''));
				} else if (typeof numericValue !== 'number') { // Handle cases where value might be non-string, non-number initially
					numericValue = parseFloat(String(numericValue).replace(/,/g, ''));
				}

				if (!isNaN(numericValue) && typeof numericValue === 'number') {
					cell.value = numericValue; // Keep as number for calculations
					cell.format = "#,##0.00"; // Apply Excel number format with commas and 2 decimal places
				}
				// If parsing fails, cell.value remains as is, to be handled by subsequent checks if applicable
			}
			// Combine fields
			if (column.combine && dataItemForRow && column.combineFields) {
				cell.value = this.getCombineFieldValue(dataItemForRow, column.combineFields);
			}
		}

		handleExcelExport = () => {
			if (!this.excelRef || !this.excelRef.save) { // Note: this.excelRef is now the exporter instance directly
				return;
			}

			const workbook = this.excelRef.workbookOptions(); // Access workbookOptions from the exporter instance
			if (!workbook.sheets || !workbook.sheets.length || !workbook.sheets[0].rows) {
				return;
			}

			const columnConfigurations = this.props.columns;
			const excelDataItems = this.excelRef.props.data; // Data used by ExcelExport component

			workbook.sheets[0].rows.forEach((row, rowIndex) => {
				if (row.type !== "data") {
					return; // Skip header/footer/group rows
				}

				const headerRowCount = workbook.sheets[0].frozenRows || 1; // Assume frozenRows indicates header rows
				const dataItemIndex = rowIndex - headerRowCount;
				const dataItemForRow = (dataItemIndex >= 0 && dataItemIndex < excelDataItems.length) ? excelDataItems[dataItemIndex] : null;

				columnConfigurations.forEach((colConfig, colConfigIndex) => {
					const cellIndexToAccess = columnConfigurations.length === row.cells.length
						? colConfigIndex
						: colConfigIndex - 1; // This logic is preserved from original

					if (cellIndexToAccess >= 0 && cellIndexToAccess < row.cells.length) {
						const cell = row.cells[cellIndexToAccess];
						if (cell) { // Ensure cell exists
							this._processCellForExport(cell, colConfig, dataItemForRow);
						}
					}
				});
			});

			this.excelRef.save(workbook);
		};

		getCombineFieldValue = (dataItem, fields) => {
			const getColumnConfig = (fieldName) => this.props.columns.find(col => col.field === fieldName);

			const fieldName1 = fields[0];
			const fieldName2 = fields[1];

			let val1 = dataItem[fieldName1];
			let val2 = dataItem[fieldName2];

			const col1Config = getColumnConfig(fieldName1);
			if (col1Config && col1Config.filterType === "numeric" && val1 !== undefined && val1 !== null && val1 !== "") {
				val1 = this.numberWithCommas(val1);
			}

			const col2Config = getColumnConfig(fieldName2);
			if (col2Config && col2Config.filterType === "numeric" && val2 !== undefined && val2 !== null && val2 !== "") {
				val2 = this.numberWithCommas(val2);
			}

			// Use original values for the conditional logic structure, but formatted values for display
			const originalVal1 = dataItem[fieldName1];
			const originalVal2 = dataItem[fieldName2];

			// Check truthiness of original values to mimic original behavior (e.g. 0 is falsy)
			if (originalVal1 && originalVal2) {
				return `${val1} / ${val2}`; // Use formatted values
			} else {
				// This replicates (A || B) behavior: if original A is truthy, use (formatted) A, else use (formatted) B.
				return originalVal1 ? val1 : val2;
			}
		};


		exportToPDF = () => {
			// Assuming this.tempRef is a ref to a PDFExport component
			if (this.tempRef?.current?.save) { // Added null check for current and save
				this.tempRef?.current.save();
			}
		}
		loadingPanel = (
			<div className="k-loading-mask">
				<span className="k-loading-text">Loading</span>
				<div className="k-loading-image"></div>
				<div className="k-loading-color"></div>
			</div>
		);
		render() {
			const dataToShow = (this.state.result)
				? this.state.result
				: this.state.data;
			const newData = setExpandedState({
				data: dataToShow,
				collapsedIds: this.state.collapsedState,
			});
			return (
				<div style={{ position: "relative" }}>
					{this.state.isLoading && this.loadingPanel}
					{this.state.error && (
						<Alert
							style={{ marginBottom: 10, marginTop: 10 }}
							type="error"
							description={this.state?.error}
							showIcon
						/>

					)}
					{this.props.showExcelExport ? (
						<ExcelExport
							data={newData}
							fileName={this.props?.pKey + ".xlsx"}
							ref={this.setExcelRef}>
							<WrappedGrid
								filterOperators={filterOperators}
								pageable={{ pageSizes: [5, 10, 20, 30, 40, 50, "All"] }}
								{...this.props}
								total={this.state.total}
								data={newData}
								skip={this.state.dataState.skip}
								pageSize={this.state.dataState.take}
								filter={this.state.dataState.filter}
								sort={this.state.dataState.sort}
								onDataStateChange={this.handleDataStateChange}>
								{this.props.children}
							</WrappedGrid>
						</ExcelExport>
					) : (
						<WrappedGrid
							resizable={true}
							filterOperators={filterOperators}
							pageable={{ pageSizes: [5, 10, 20, 30, 40, 50, "All"] }}
							{...this.props}
							total={this.state.total}
							data={newData}
							skip={this.state.dataState.skip}
							pageSize={this.state.dataState.take}
							filter={this.state.dataState.filter}
							sort={this.state.dataState.sort}
							onDataStateChange={this.handleDataStateChange}
						>

						</WrappedGrid>
					)}
				</div>
			);
		}

		componentDidMount() {
			this.fetchData(this.state.dataState);
		}
		componentDidUpdate(prevProps) {
			if (this.props.showExcelExport !== prevProps.showExcelExport) {
				if (this.props.showExcelExport) {
					this.handleExcelExport();
				}
			}
			else if (JSON.stringify(this.props.additionalParams) !== JSON.stringify(prevProps.additionalParams)) {
				this.fetchData(this.initialState)
			}
			else if (this.props.url !== prevProps.url) {
				this.fetchData(this.initialState);
			}
		}
		handleDataStateChange = (changeEvent) => {
			const isObjectEqual = (obj1, obj2) => {
				const obj1Keys = Object.keys(obj1);
				const obj2Keys = Object.keys(obj2);
				if (obj1Keys.length !== obj2Keys.length) return false;
				for (const key of obj1Keys) {
					if (obj1[key] !== obj2[key]) return false;
				}
				return true;
			}
			const didArrayChange = (arr1, arr2) => {
				if ((arr1 === undefined && arr2 === undefined) || (arr1 && !arr1.length && !arr2) || (arr2 && !arr2.length && !arr1)) {
					return false;
				}
				if (!arr1 || !arr2) return true;
				if (arr1.length !== arr2.length) return true;
				for (let i = 0; i < arr1.length; i++) {
					if (!isObjectEqual(arr1[i], arr2[i])) return true;
				}
				return false;
			};
			const applyDataToGrid = (changeEvents) => {
				let _dataState = changeEvents.dataState;
				if (isNaN(_dataState.take)) {
					_dataState.take = this.state.total;
				}
				if (changeEvents.dataState?.filter) {
					const processedFilters = this.trimFilterValues(changeEvents.dataState.filter.filters);
					_dataState.filter = {
						...this?.props.dataState?.filter,
						...changeEvents.dataState?.filter,
						filters: processedFilters || []
					};
				}
				this.setState({ dataState: _dataState });
				this.fetchData(_dataState);
			}

			const { group: prevGroup } = this.state.dataState
			const { group: eventGroup } = changeEvent.dataState
			applyDataToGrid(changeEvent)

			if (didArrayChange(prevGroup, eventGroup)) {
				const newDataState = processWithGroups(this.state.ungroupedData.length > 0 ? this.state.ungroupedData : this.state.data, changeEvent.dataState.group);
				this.setState({
					collapsedState: [],
					result: changeEvent.dataState.group.length > 0 ? newDataState : undefined,
					group: changeEvent.dataState.group,
				});
			}
			this.props.getDataStateFromChild(changeEvent.dataState)
		};
		_encrypt(msg, key) {
			msg = typeof msg == "object" ? JSON.stringify(msg) : msg;
			const salt = CryptoJS.lib.WordArray.random(128 / 8);

			const derivedKey = CryptoJS.PBKDF2(key, salt, {
				keySize: 256 / 32,
				iterations: 10,
			});

			const iv = CryptoJS.lib.WordArray.random(128 / 8);

			const encrypted = CryptoJS.AES.encrypt(msg, derivedKey, {
				iv: iv,
				padding: CryptoJS.pad.Pkcs7,
				mode: CryptoJS.mode.CBC,
			});
			return salt.toString() + iv.toString() + encrypted.toString();
		}
		getstateParams = (obj) => {
			if (this.props.isABpCall) {
				const replaceKeys = { 'skip': 'skipCount', 'take': "maxResultCount" }
				const queryList = Object.entries(obj)
					.filter(([key, value]) => value !== undefined && value !== null)
					.map(([key, value]) => {
						if (key === 'sort') {
							return "sorting=" + value.map(item => `${item.field} ${item.dir}`)
						} else {

							return `${encodeURIComponent(replaceKeys[key] || key)}=${encodeURIComponent(value)}`
						}
					});
				return queryList.join('&');
			} else {
				return toDataSourceRequestString(obj)
			}

		}
		trimFilterValues = (filters) => {
			if (!filters || !Array.isArray(filters)) return [];

			const seenFilters = new Set();

			return filters
				.map(filter => {
					if (!filter) return null;

					if (Array.isArray(filter.filters)) {
						return {
							...filter,
							filters: this.trimFilterValues(filter.filters)
						};
					}

					const filterKey = JSON.stringify({
						field: filter.field,
						operator: filter.operator,
						value: typeof filter.value === 'string' ? filter.value.trim() : filter.value
					});

					if (seenFilters.has(filterKey)) return null;
					seenFilters.add(filterKey);

					return {
						...filter,
						value: typeof filter.value === 'string' ? filter.value.trim() : filter.value
					};
				})
				.filter(Boolean);
		};
		fetchData(dataState) {
			if (dataState.filter) {
				dataState.filter.filters?.map((item) => {
					item.filters?.map((value) => {
						if (
							value.operator === "gte" ||
							value.operator === "gt" ||
							value.operator === "lte" ||
							value.operator === "lt"
						) {
							value.value = value.value && (
								value.operator === "lte" || value.operator === "gt"
									? new Date(moment(value.value).format("YYYY-MM-DDT23:59:59"))
									: new Date(moment(value.value).format("YYYY-MM-DDT00:00:00"))
							);
						}
					});
				});
			}
			this.setState({ ...this.state, isLoading: true });
			const {
				profile: { access_token },
			} = store.getState().oidc
			let queryStr = `${this.getstateParams(dataState)}`;

			if (this.props.additionalParams) {
				let _additionalParams = "";
				if (this.props.isABpCall) {
					_additionalParams = Object.entries(this.props.additionalParams)
						.filter(([key, value]) => value !== undefined && value !== null)
						.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
						.join('&');
					queryStr = '?' + _additionalParams + (_additionalParams && queryStr ? '&' : '') + queryStr; // Ensure '&' is only added if both params exist
				} else {
					for (let key in this.props.additionalParams) {
						_additionalParams = _additionalParams + `/${this.props.additionalParams[key]}`
					}
					queryStr = _additionalParams + (queryStr ? '?' : '') + queryStr; // Ensure '?' is only added if queryStr exists
				}
			} else {
				queryStr = (queryStr ? "?" : "") + queryStr; // Add '?' only if queryStr is not empty
			}
			const base_url = this.props.url;
			const Authorization = `Bearer ${access_token}`;
			const init = {
				method: "GET",
				headers: {
					Authorization: Authorization,
					'Content-Type': 'application/json',
					'Accept': 'application/json',
				},
			};
			fetch(`${base_url}${queryStr}`, init)
				.then(response => {
					if (response.ok) {
						this.setState({ ...this.state, gridStatus: response?.status, error: null })
						return response.json()
					} else {
						this.setState({ ...this.state, error: apiCalls.isErrorDispaly(response), isLoading: false, data: [] })
						return Promise.reject(response); // Ensure promise chain is broken correctly on error
					}
				})
				.then(responseBody => { // Process the parsed JSON response body
					let apiItems = [];
					let apiTotal = 0;

					// Check for Format 1: { "totalCount": ..., "items": [...] }
					if (responseBody && typeof responseBody.totalCount !== 'undefined' && responseBody.items !== undefined) {
						apiItems = Array.isArray(responseBody.items) ? responseBody.items : [];
						apiTotal = Number(responseBody.totalCount) || 0;
					}
					// Check for Format 2: { "data": [...] } (possibly with "total")
					else if (responseBody && responseBody.data !== undefined) {
						apiItems = Array.isArray(responseBody.data) ? responseBody.data : [];
						apiTotal = typeof responseBody.total !== 'undefined' ? Number(responseBody.total) : apiItems.length;
					} else {
						console.error("Unexpected API response structure:", responseBody);
						this.setState({
							...this.state,
							error: "Invalid data structure received from server.",
							isLoading: false, data: [], total: 0, result: undefined, ungroupedData: []
						});
						return; // Stop processing if format is not recognized
					}

					// Helper function to flatten data if it's in Kendo's grouped format or contains nested 'items'.
					// It aims to return a flat list of individual data records.
					const extractFlatRecords = (dataToFlatten) => {
						let results = [];
						if (!Array.isArray(dataToFlatten)) return results;
						dataToFlatten.forEach(item => {
							if (typeof item === 'object' && item !== null) {
								if (item.items && Array.isArray(item.items)) { // It's a Kendo-style group item
									results = results.concat(extractFlatRecords(item.items));
								} else { // It's a data item or a group item without valid 'items'
									results.push(item);
								}
							} else {
								results.push(item); // Include non-object items as well
							}
						});
						return results;
					};

					const clientSideGroupingActive = this.state.group && this.state.group.length > 0;
					const flatDataForClientProcessing = extractFlatRecords(apiItems);

					let processedDataForState;
					let clientGroupedResultForState;

					if (clientSideGroupingActive) {
						clientGroupedResultForState = processWithGroups(flatDataForClientProcessing, this.state.group);
						// Per original logic: if client grouping, translate apiItems for this.state.data
						processedDataForState = translateDataSourceResultGroups(apiItems);
					} else {
						clientGroupedResultForState = undefined;
						// Per original logic: if no client grouping, use raw apiItems for this.state.data
						processedDataForState = apiItems;
					}

					this.setState({
						ungroupedData: flatDataForClientProcessing,
						data: processedDataForState,
						total: apiTotal,
						dataState,
						error: null,
						isLoading: false,
						result: clientGroupedResultForState,
					});
				})
				.catch(err => {
					const status = err?.status || this.state?.gridStatus;
					if (status === 401) {
						this.props.history?.push("/accessdenied");
					} else {
						if (!this.state.error) {
							this.setState({ ...this.state, error: apiCalls.isErrorDispaly(err), isLoading: false })
						}
					}
				})
		}
	}
}
